import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Search, FileText, Clipboard, Merge } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

const ALL_DOCS = [
  { key: "boletim_operatorio", label: "Boletim" },
  { key: "prescricao_medica", label: "Prescrição" },
  { key: "aih", label: "AIH" },
  { key: "histopatologico", label: "Histopatológico" },
  { key: "tcle", label: "TCLE" },
];

export default function ProceduresPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get("/procedures", { params: search ? { search } : {} });
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  // load closes over `search` — intentional debounced refetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  function openNew() {
    setForm({
      name: "", specialty: "Cirurgia Geral", sus_code: "", cid_codes_text: "",
      default_diagnosis: "",
      required_documents: ["boletim_operatorio", "prescricao_medica", "aih", "tcle"],
      active: true,
    });
    setNewOpen(true);
  }

  async function saveNew() {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        specialty: form.specialty,
        sus_code: form.sus_code || null,
        cid_codes: (form.cid_codes_text || "").split(",").map((s) => s.trim()).filter(Boolean),
        default_diagnosis: form.default_diagnosis || null,
        required_documents: form.required_documents,
        active: !!form.active,
      };
      const r = await api.post("/procedures", payload);
      toast.success("Procedimento criado. Agora personalize os documentos.");
      setNewOpen(false);
      nav(`/procedimentos/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  function toggleDoc(key) {
    const curr = form.required_documents || [];
    const next = curr.includes(key) ? curr.filter((x) => x !== key) : [...curr, key];
    setForm({ ...form, required_documents: next });
  }

  // Merge dialog state
  const [mergeTarget, setMergeTarget] = useState(null); // canonical procedure
  const [mergeSelected, setMergeSelected] = useState(new Set());
  const [merging, setMerging] = useState(false);

  async function runMerge() {
    if (!mergeTarget || mergeSelected.size === 0) return;
    const names = items.filter((p) => mergeSelected.has(p.id)).map((p) => p.name).join(", ");
    if (!(await confirm({ description: `Mesclar "${names}" em "${mergeTarget.name}"?\n\n` +
      `• Os procedimentos de origem serão EXCLUÍDOS permanentemente.\n` +
      `• Seus nomes viram apelidos de "${mergeTarget.name}".\n` +
      `• Casos e mapas que usavam os originais passarão a usar "${mergeTarget.name}".\n\n` +
      `A ação é irreversível. Continuar?` }))) return;
    setMerging(true);
    try {
      const r = await api.post(`/procedures/${mergeTarget.id}/merge`, {
        source_ids: Array.from(mergeSelected),
      });
      const s = r.data.merge_stats || {};
      toast.success(
        `Mesclagem concluída: ${s.merged} procedimento(s) excluído(s), ` +
        `${s.aliases_added} apelido(s), ${s.cases_updated} caso(s) atualizado(s), ` +
        `${s.rows_updated} linha(s) de mapa atualizada(s).`
      );
      setMergeTarget(null);
      setMergeSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMerging(false); }
  }

  return (
    <div className="space-y-5" data-testid="procedures-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Procedimentos</h1>
          <p className="text-sm text-slate-500 mt-1">
            Catálogo de procedimentos cirúrgicos. Clique em um para editar os documentos (Boletim, Prescrição, AIH, Histopatológico, TCLE).
          </p>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="procedures-btn-new">
              <Plus className="h-4 w-4 mr-2" /> Novo procedimento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="proc-dialog">
            <DialogHeader><DialogTitle>Novo procedimento</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0 pr-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 space-y-1.5"><Label>Nome *</Label>
                  <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: Colecistectomia Convencional" data-testid="proc-input-name" /></div>
                <div className="space-y-1.5"><Label>Especialidade</Label>
                  <Input value={form.specialty || ""} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Código SUS</Label>
                  <Input value={form.sus_code || ""} onChange={(e) => setForm({ ...form, sus_code: e.target.value })} /></div>
                <div className="md:col-span-2 space-y-1.5"><Label>CID-10 (separe por vírgula)</Label>
                  <Input value={form.cid_codes_text || ""} onChange={(e) => setForm({ ...form, cid_codes_text: e.target.value })} placeholder="K80.0, K81.1" /></div>
                <div className="md:col-span-2 space-y-1.5"><Label>Diagnóstico padrão</Label>
                  <Textarea rows={2} value={form.default_diagnosis || ""} onChange={(e) => setForm({ ...form, default_diagnosis: e.target.value })} /></div>
              </div>
              <div className="space-y-2">
                <Label>Documentos obrigatórios</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {ALL_DOCS.map((d) => (
                    <label key={d.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={(form.required_documents || []).includes(d.key)} onCheckedChange={() => toggleDoc(d.key)} />
                      {d.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewOpen(false)}>Cancelar</Button>
              <Button onClick={saveNew} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="proc-btn-save">
                {saving ? "Salvando..." : "Criar e editar documentos"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Buscar procedimento..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="procedures-search" />
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Procedimento</th>
                <th className="text-left px-4 py-3">Especialidade</th>
                <th className="text-left px-4 py-3">Código SUS</th>
                <th className="text-left px-4 py-3">CID</th>
                <th className="text-left px-4 py-3">Documentos</th>
                <th className="text-left px-4 py-3">Ativo</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="text-center p-8 text-slate-400">Nenhum procedimento cadastrado.</td></tr>}
              {items.map((p, idx) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => nav(`/procedimentos/${p.id}`)} data-testid={`procedures-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <Clipboard className="h-3.5 w-3.5 text-slate-400" />
                      {p.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{p.specialty || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.sus_code || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{(p.cid_codes || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {(p.required_documents || []).map((d) => {
                        const label = ALL_DOCS.find((x) => x.key === d)?.label || d;
                        return <span key={d} className="text-[10px] font-medium bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{label}</span>;
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3">{p.active ? "Sim" : "Não"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => { setMergeTarget(p); setMergeSelected(new Set()); }} title="Mesclar outros procedimentos neste" data-testid={`proc-merge-${idx}`}>
                        <Merge className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => nav(`/procedimentos/${p.id}`)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Merge dialog */}
      <Dialog open={!!mergeTarget} onOpenChange={(v) => !v && setMergeTarget(null)}>
        <DialogContent className="max-w-2xl" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>Mesclar procedimentos em "{mergeTarget?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              Selecione abaixo os procedimentos que devem ser <span className="font-medium">absorvidos</span> em
              "<span className="font-medium">{mergeTarget?.name}</span>". Seus nomes viram apelidos e todos os casos/mapas que os referenciam passam a usar "{mergeTarget?.name}".
            </p>
            <div className="border border-slate-200 rounded max-h-[50vh] overflow-y-auto">
              {items.filter((p) => p.id !== mergeTarget?.id).map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={mergeSelected.has(p.id)}
                    onCheckedChange={(v) => {
                      const next = new Set(mergeSelected);
                      if (v) next.add(p.id); else next.delete(p.id);
                      setMergeSelected(next);
                    }}
                    data-testid={`merge-select-${p.id}`}
                  />
                  <span className="font-medium text-slate-800">{p.name}</span>
                  <span className="text-xs text-slate-500">{p.specialty}</span>
                  {p.sus_code && <span className="text-[10px] font-mono text-slate-400 ml-auto">{p.sus_code}</span>}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeTarget(null)}>Cancelar</Button>
            <Button
              onClick={runMerge}
              disabled={merging || mergeSelected.size === 0}
              className="bg-rose-700 hover:bg-rose-800"
              data-testid="btn-run-merge"
            >
              <Merge className="h-4 w-4 mr-2" />
              {merging ? "Mesclando..." : `Mesclar ${mergeSelected.size} procedimento(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
